import { GridItem, Skeleton } from "@chakra-ui/react"
import { useQueryWithEndpoint } from "@hydrologiq/shared/hooks/useGraphQLClient"
import type { InstallationHeadlines } from "@hydrologiq/gql-experience/graphql"
import { GetInstallationHeadlinesDocument } from "@hydrologiq/gql-experience/graphql"
import { toTimestamp } from "@hydrologiq/common/helpers/date"
import { DateTime } from "timezonecomplete"
import FigureGrid from "@hydrologiq/shared/figures/generic/FigureGrid"
import { useEffect, useState } from "react"
import HydrogenLevelFigure from "../figures/headlines/HydrogenLevelFigure"
import HydrogenUsedFigure from "../figures/headlines/HydrogenUsedFigure"
import EnergyGeneratedFigure from "../figures/headlines/EnergyGeneratedFigure"
import EmissionsFactorFigure from "../figures/headlines/EmissionsFactorFigure"
import TotalEmissionsFigure from "../figures/headlines/TotalEmissionsFigure"
import CurrentPowerFigure from "../figures/headlines/CurrentPowerFigure"
import NextRefuelFigure from "../figures/headlines/NextRefuelFigure"
import { Figure as FigureType } from "@hydrologiq/schemas/1.0.0/installationPage"
import { FIGURE_KEYS, FIGURE_TYPE_TO_KEY, type FigureKey } from "@hydrologiq/shared/figures/figureKeys"

interface InstallationHeadlineFiguresProps {
  installationId?: string
  stop?: DateTime
  refreshVersion?: number
  figures?: FigureType[]
}

const InstallationHeadlineFigures = ({ installationId, stop, refreshVersion, figures }: InstallationHeadlineFiguresProps) => {
  const { data, loading, error, refetch, networkStatus } = useQueryWithEndpoint(
    GetInstallationHeadlinesDocument,
    {
      variables: {
        installationId: installationId ?? "",
        stop: stop ? toTimestamp(stop) : undefined,
      },
      skip: !installationId,
    },
    "primary" // Use the primary (experience API) endpoint
  )

  useEffect(() => {
    if (refreshVersion) refetch()
  }, [refreshVersion])

  const [headlines, setHeadlines] = useState<InstallationHeadlines | null>(null)

  useEffect(() => {
    if (data?.getInstallationHeadlines) {
      setHeadlines(data.getInstallationHeadlines)
    }
  }, [data])

  // Build array of figures with their components
  const availableFigures = []
  const allowedFigures = headlines?.allowedFigures || []

  // Helper function to check if a figure should be shown
  const shouldShowFigure = (figureKey: FigureKey) => {
    if (figures) {
      return figures.map((figure) => FIGURE_TYPE_TO_KEY[figure.type]).some((key) => key === figureKey)
    }
    return allowedFigures.includes(figureKey)
  }

  if (headlines) {
    // Run-dry (Run Dry)
    if (shouldShowFigure(FIGURE_KEYS.NEXT_REFUEL)) {
      availableFigures.push({
        key: "nextRefuel",
        component: (
          <NextRefuelFigure
            data={headlines.nextRefuel ?? undefined}
            loading={loading}
            error={error?.message}
            isPlaceholder={!headlines.nextRefuel?.available}
            compact
          />
        ),
      })
    }

    // Hydrogen level
    if (shouldShowFigure(FIGURE_KEYS.HYDROGEN_LEVEL)) {
      availableFigures.push({
        key: "hydrogenLevel",
        component: (
          <HydrogenLevelFigure
            data={headlines.hydrogenLevel ?? undefined}
            loading={loading}
            error={error?.message}
            isPlaceholder={!headlines.hydrogenLevel?.available}
            compact
          />
        ),
      })
    }

    // Hydrogen used
    if (shouldShowFigure(FIGURE_KEYS.HYDROGEN_USED)) {
      availableFigures.push({
        key: "hydrogenUsed",
        component: (
          <HydrogenUsedFigure
            data={headlines.hydrogenUsed ?? undefined}
            loading={loading}
            error={error?.message}
            isPlaceholder={!headlines.hydrogenUsed?.available}
            compact
          />
        ),
      })
    }

    // Energy
    if (shouldShowFigure(FIGURE_KEYS.ENERGY)) {
      availableFigures.push({
        key: "energy",
        component: (
          <EnergyGeneratedFigure
            data={headlines.energy ?? undefined}
            loading={loading}
            error={error?.message}
            isPlaceholder={!headlines.energy?.available}
            compact
          />
        ),
      })
    }

    // Emissions factor
    if (shouldShowFigure(FIGURE_KEYS.EMISSIONS_FACTOR)) {
      availableFigures.push({
        key: "emissionsFactor",
        component: (
          <EmissionsFactorFigure
            data={headlines.emissionsFactor ?? undefined}
            loading={loading}
            error={error?.message}
            isPlaceholder={!headlines.emissionsFactor?.available}
            compact
          />
        ),
      })
    }


    // Current power
    if (shouldShowFigure(FIGURE_KEYS.CURRENT_POWER)) {
      availableFigures.push({
        key: "currentPower",
        component: (
          <CurrentPowerFigure
            data={headlines.currentPower ?? undefined}
            loading={loading}
            error={error?.message}
            isPlaceholder={!headlines.currentPower?.available}
          />
        ),
      })
    }


    // CO2e
    if (shouldShowFigure(FIGURE_KEYS.CO2E)) {
      availableFigures.push({
        key: "co2e",
        component: (
          <TotalEmissionsFigure
            data={headlines.co2e ?? undefined}
            loading={loading}
            error={error?.message}
            isPlaceholder={!headlines.co2e?.available}
            compact
          />
        ),
      })
    }
  }

  // Show skeleton on initial loading or when query is skipped, but not on refetch
  const shouldShowSkeleton = networkStatus === 1 || !installationId || error || !headlines

  return shouldShowSkeleton ? (
    <Skeleton height="175px" width="100%" />
  ) : (
    <FigureGrid>
      {availableFigures.map(({ key, component }) => (
        <GridItem key={key}>{component}</GridItem>
      ))}
    </FigureGrid>
  )
}

export default InstallationHeadlineFigures
